import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import {
  ConsolidatedCoinData,
  ConsolidatedCoinDataWithKey,
  LanguageNameSpaces,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { DISPLAY_CURRENCIES } from "common/constants";
import { EmptyStatesType } from "common/Icons/types";
import EmptyView from "common/Empty/EmptyView";
import FlatList from "common/Lists/FlatList";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import { useAssetsData } from "features/portfolio/common/hooks";
import {
  getTopShares,
  PhoneAllocationCard,
  PhoneSplitSegment,
  phoneMoney,
  phonePercent,
  usePhoneRestColor,
} from "features/dashboard/PhoneDashboard/PhoneSummary";
import {
  PhoneCardSlot,
  PhoneSectionAction,
  PhoneSectionHead,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import NewLookPositionCard, {
  NewLookPositionCardSkeleton,
} from "features/portfolio/NewLook/NewLookPositionCard.tsx";
import { RootState } from "app/store";
import { getPositionFigures } from "features/portfolio/NewLook/positionFigures.ts";

interface PhonePositionsProps {
  dataSelector: (state: RootState) => ConsolidatedCoinData[];
  searchValue?: string;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  /** Opens the buy modal (the inline entry: no FAB on the new-look phone). */
  onBuyClick?: () => void;
}

type ListItem = ConsolidatedCoinDataWithKey | { key: string; skeleton: true };

const isSkeleton = (item: ListItem): item is { key: string; skeleton: true } =>
  "skeleton" in item;

/**
 * Phone positions tab: the total unrealized PnL in a hero card, then each
 * position as its own card (the desktop position card).
 */
const PhonePositions = ({
  dataSelector,
  searchValue,
  loading,
  refreshing,
  onRefresh,
  onBuyClick,
}: PhonePositionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const restColor = usePhoneRestColor();
  const isBtc = useSelector(selectDisplayCurrency) === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const { rawPortfolioData, filteredCoinsData: all } = useAssetsData({
    dataSelector,
    isPositions: true,
  });
  const { sortedCoinsData: rows } = useAssetsData({
    dataSelector,
    searchFilter: searchValue,
    isPositions: true,
  });

  const summary = useMemo(() => {
    let pnl = 0;
    let margin = 0;
    let winning = 0;
    let best: { symbol: string; pnl: number } | undefined;
    all.forEach((position) => {
      const figures = getPositionFigures(position, isBtc);
      pnl += figures.pnl;
      margin += figures.margin;
      if (figures.pnl > 0) winning += 1;
      if (!best || figures.pnl > best.pnl)
        best = { symbol: position.symbol, pnl: figures.pnl };
    });
    const shares = getTopShares(
      all.map((position) => ({
        key: String(position.key),
        name: position.symbol,
        group: position.symbol,
        value: getPositionFigures(position, isBtc).margin,
      })),
      3,
      t("phone.other"),
      restColor
    );
    let longMargin = 0;
    let shortMargin = 0;
    all.forEach((position) => {
      const value = getPositionFigures(position, isBtc).margin;
      if (position.is_short) shortMargin += value;
      else longMargin += value;
    });
    const exposureTotal = longMargin + shortMargin;
    const exposure: PhoneSplitSegment[] = exposureTotal
      ? [
          {
            key: "long",
            name: t("positions.long"),
            color: tokens.up,
            share: longMargin / exposureTotal,
          },
          {
            key: "short",
            name: t("positions.short"),
            color: tokens.down,
            share: shortMargin / exposureTotal,
          },
        ].filter((segment) => segment.share > 0)
      : [];
    return {
      pnl,
      margin,
      winning,
      shares,
      exposure,
      roi: margin ? (pnl / margin) * 100 : 0,
    };
  }, [all, isBtc, restColor, t, tokens]);

  const pending = !!loading && !all.length;
  const showHero = pending || all.length > 0;
  const items: ListItem[] =
    loading && !rows.length
      ? [0, 1, 2, 3].map((index) => ({
          key: `skeleton-${index}`,
          skeleton: true,
        }))
      : rows;

  const emptyState = !loading ? (
    <EmptyView
      iconKey={EmptyStatesType.Coins}
      title={t("positions.empty_title")}
      iconWidth={100}
      objectName="positions"
      isSearch={!!searchValue?.length}
      hasData={!!rawPortfolioData?.length}
      filtersSlice={portfolioFilters}
    />
  ) : undefined;

  const renderTop = () => (
    <>
      {showHero && (
        <PhoneAllocationCard
          label={t("phone.unrealized_pnl")}
          value={phoneMoney(summary.pnl, symbol, precision, true)}
          valueColor={directionColor(tokens, summary.pnl)}
          loading={pending}
          meta={t("phone.on_margin", {
            percent: phonePercent(summary.roi),
          })}
          segments={summary.exposure}
        />
      )}
      {(pending || rows.length > 0) && (
        <PhoneSectionHead
          title={t("phone.positions_title")}
          extra={!pending && <Count>{rows.length}</Count>}
          trailing={
            onBuyClick && (
              <PhoneSectionAction type="button" onClick={onBuyClick}>
                {t("buttons.buy")}
              </PhoneSectionAction>
            )
          }
        />
      )}
    </>
  );

  return (
    <FlatList
      data={items}
      renderTopContainer={renderTop}
      renderContent={({ item }) => (
        <PhoneCardSlot>
          {isSkeleton(item) ? (
            <NewLookPositionCardSkeleton />
          ) : (
            <NewLookPositionCard coinData={item} />
          )}
        </PhoneCardSlot>
      )}
      gap={0}
      paddingHorizontal={0}
      wrapperStyle={WRAPPER_STYLE}
      refreshing={refreshing}
      onRefresh={onRefresh}
      emptyState={emptyState}
    />
  );
};

export default PhonePositions;

const WRAPPER_STYLE = { padding: "0 0 8px" };

const Count = styled.span`
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
